import { useEffect, useState } from "react";

const memorizedStates = new WeakMap<object, any>();

function getMemorizedState<T>(obj: object, key: string): T | undefined {
    if (memorizedStates.has(obj)) {
        return memorizedStates.get(obj)[key];
    }
    return undefined;
}

function setMemorizedState<T>(obj: object, key: string, value: T): void {
    if (!memorizedStates.has(obj)) {
        memorizedStates.set(obj, {});
    }
    memorizedStates.get(obj)[key] = value;
}

/**
 * Keep state in memory across component re-renders.
 * This is useful for persisting state that should not be lost
 * when the component is re-rendered, such as form inputs, user selections and navigation.
 */
export default function useMemorizedState<T>(
    initialState: T | (() => T),
    key: string,
    deps: React.DependencyList = [],
): [T, (newState: T | ((prevState: T) => T)) => Promise<void>] {
    const [state, setState] = useState<T>(() => {
        const memorized = getMemorizedState<T>(useMemorizedState, key);
        if (memorized !== undefined) {
            return memorized;
        }
        const initial =
            typeof initialState === "function"
                ? (initialState as () => T)()
                : initialState;
        setMemorizedState(useMemorizedState, key, initial);
        return initial;
    });

    useEffect(() => {
        const memorized = getMemorizedState<T>(useMemorizedState, key);
        if (memorized !== undefined) {
            setState(memorized);
        } else {
            setMemorizedState(useMemorizedState, key, state);
        }
        // biome-ignore lint/correctness/useExhaustiveDependencies: // Re-initialize memorized initial state when deps change
    }, deps);

    const setMemorized = async (newState: T | ((prevState: T) => T)) => {
        setState((prevState) => {
            const nextState =
                typeof newState === "function"
                    ? (newState as (prev: T) => T)(prevState)
                    : newState;
            setMemorizedState(useMemorizedState, key, nextState);
            return nextState;
        });
        // Ensure the state is set before the next render
        await new Promise((resolve) => setTimeout(resolve, 0));
    };

    return [state, setMemorized] as const;
}
